/**
 * Row helpers: Output, Evaluations and Source. Pure functions so the grid
 * cells, the drawer and the tests share one reading.
 */

/** Flat text of a message content (string, or parts with text / image / json). */
export function contentText(content) {
  if (content == null) return '';
  if (typeof content === 'string') return content;
  if (Array.isArray(content)) {
    return content
      .map((p) => {
        if (!p) return '';
        if (typeof p === 'string') return p;
        if (p.type === 'text') return p.text || '';
        if (p.type === 'image') return '[image]';
        if (p.type === 'json') return JSON.stringify(p.value);
        return p.text || '';
      })
      .filter(Boolean)
      .join('\n');
  }
  if (typeof content === 'object') return JSON.stringify(content);
  return String(content);
}

/** What the unit produced, as text: the assistant message's content, else the JSON. */
export function outputText(output) {
  if (output == null) return '';
  if (typeof output === 'string') return output;
  if (typeof output === 'object' && !Array.isArray(output) && ('content' in output || 'tool_calls' in output)) {
    const text = contentText(output.content);
    const calls = Array.isArray(output.tool_calls) && output.tool_calls.length
      ? output.tool_calls.map((t) => `→ ${t.function?.name || t.name || 'tool'}(${t.function?.arguments || JSON.stringify(t.arguments || {})})`).join('\n')
      : '';
    return [text, calls].filter(Boolean).join('\n');
  }
  return JSON.stringify(output);
}

/** A row's unit: what it is about and where it came from. */
export function unitOf(item) {
  const ref = item?.source_trace_ref && typeof item.source_trace_ref === 'object' ? item.source_trace_ref : {};
  const kind = item?.unit_kind || (item?.source_eval_run_id ? 'verdict' : item?.source_span_id ? 'span' : item?.source_run_id ? 'run' : item?.source_conversation_id ? 'conversation' : 'manual');
  return {
    kind,
    eval_run_id: item?.source_eval_run_id || ref.eval_run_id || null,
    lineage_id: ref.lineage_id || null,
    eval_name: ref.eval_name || null,
    eval_version: ref.eval_version || null,
    unit_expired: Boolean(ref.unit_expired),
    span_id: item?.source_span_id || (kind === 'span' ? ref.span_id : null) || null,
    run_id: item?.source_run_id || ref.run_id || null,
    conversation_id: item?.source_conversation_id || ref.conversation_id || null,
    agent: ref.agent || ref.agent_name || null,
    span_name: ref.span_name || null,
    started_at: ref.started_at || null,
    ref,
  };
}

const shortId = (id) => String(id || '').slice(0, 8);

/**
 * The links a row carries, in the order the Links column shows them: the eval
 * (a verdict row), then the run, the span and the conversation it is about.
 * Each is { kind, label, title, open } where `open` is what onOpenOrigin takes.
 */
export function linksOf(unit) {
  if (!unit || unit.kind === 'manual') return [];
  const out = [];
  if (unit.kind === 'verdict' && unit.eval_run_id) {
    out.push({
      kind: 'eval', label: `${unit.eval_name || 'Eval'}${unit.eval_version ? ` v${unit.eval_version}` : ''}`, title: `Open the verdict ${unit.eval_run_id}`,
      open: { kind: 'verdict', eval_run_id: unit.eval_run_id, lineage_id: unit.lineage_id, run_id: unit.run_id, span_id: unit.span_id },
    });
  }
  if (unit.run_id) out.push({ kind: 'run', label: `Run ${shortId(unit.run_id)}`, title: `Open run ${unit.run_id}`, open: { kind: 'run', run_id: unit.run_id } });
  if (unit.span_id) out.push({ kind: 'span', label: unit.span_name ? `Span ${unit.span_name}` : `Span ${shortId(unit.span_id)}`, title: `Open span ${unit.span_id}`, open: { kind: 'span', span_id: unit.span_id, run_id: unit.run_id } });
  if (unit.conversation_id) {
    out.push({
      kind: 'conversation', label: `Conversation ${shortId(unit.conversation_id)}`, title: `Open conversation ${unit.conversation_id}`,
      open: { kind: 'conversation', conversation_id: unit.conversation_id, run_id: unit.run_id, from: unit.started_at || null, to: unit.ref?.last_run_at || null },
    });
  }
  return out;
}

/** Metadata keys that are ids of something the row links to, and what each opens. */
export const LINK_KEYS = {
  run_id: (v) => ({ kind: 'run', run_id: v }),
  span_id: (v) => ({ kind: 'span', span_id: v }),
  conversation_id: (v) => ({ kind: 'conversation', conversation_id: v }),
};

/** Short text for one evaluation chip: "Answers the question · fail". */
export function evaluationChip(ev) {
  const outcome = ev.state && ev.state !== 'resolved' ? ev.state : (ev.passed === true ? 'pass' : ev.passed === false ? 'fail' : '—');
  const thumb = ev.feedback?.thumbs === 'down' ? '👎' : ev.feedback?.thumbs === 'up' ? '👍' : '';
  return { name: ev.name || 'Eval', outcome, thumb, title: [ev.name, ev.version ? `v${ev.version}` : null, outcome, ev.reason_code, ev.target === 'run' ? 'on the run' : null].filter(Boolean).join(' · ') };
}

/** One step of an eval's walk in words: "Tokens used: 2784 → Far over budget". */
export function stepText(s) {
  if (!s) return '';
  const v = s.value;
  const val = v == null ? '' : typeof v === 'object' ? Object.entries(v).map(([k, x]) => `${k} ${typeof x === 'object' ? JSON.stringify(x) : x}`).join(', ') : String(v);
  const answers = s.answers && typeof s.answers === 'object' ? Object.entries(s.answers).map(([k, x]) => `${k}: ${typeof x === 'object' ? JSON.stringify(x) : x}`).join('; ') : '';
  const body = [val, answers, s.score != null && !val ? `score ${s.score}` : '', s.error ? `error: ${s.error}` : ''].filter(Boolean).join(' · ');
  return `${s.node || s.node_id || 'step'}${body ? `: ${body}` : ''}${s.edge ? ` → ${s.edge}` : ''}`;
}

/**
 * An evaluation as text lines for the table: the verdict first (name, outcome,
 * score, reason code, a thumb), then every step's decision, then the judge's
 * reasoning when there is one. Returns { head, lines }.
 */
export function evaluationLines(ev) {
  const c = evaluationChip(ev);
  const head = [c.name, ev.version ? `v${ev.version}` : null, c.outcome.toUpperCase(), typeof ev.score === 'number' ? ev.score : null, ev.reason_code || null, c.thumb || null, ev.target === 'run' ? 'on the run' : null]
    .filter((x) => x != null && x !== '').join(' · ');
  const lines = (Array.isArray(ev.steps) && ev.steps.length ? ev.steps.map(stepText) : (Array.isArray(ev.path) ? [ev.path.join(' → ')] : [])).filter(Boolean);
  if (ev.judge_reasoning) lines.push(`judge: ${ev.judge_reasoning}`);
  if (ev.feedback?.reason) lines.push(`${ev.feedback.thumbs === 'down' ? 'marked wrong' : 'marked right'}: ${ev.feedback.reason}`);
  return { head, lines, outcome: c.outcome, title: c.title };
}

/** The Evaluations cell as plain text (for row heights and CSV). */
export function evaluationsText(evaluations) {
  const list = Array.isArray(evaluations) ? evaluations : [];
  return list.map((ev) => { const { head, lines } = evaluationLines(ev); return [head, ...lines.map((l) => `  ${l}`)].join('\n'); }).join('\n');
}

/** Summary of a row's evaluations for sorting/filtering: { total, failed, down } */
export function evaluationSummary(evaluations) {
  const list = Array.isArray(evaluations) ? evaluations : [];
  return {
    total: list.length,
    failed: list.filter((e) => e.passed === false).length,
    down: list.filter((e) => e.feedback?.thumbs === 'down').length,
  };
}

/** The roles a transcript can carry, in the words the grid shows. */
export const ROLE_TEXT = { system: 'system', user: 'user', assistant: 'assistant', tool: 'tool', function: 'tool', developer: 'system' };

/**
 * One line for the Input cell: the last user turn (what was asked), with the
 * roles present as chips; or the variables as `key: value` when there is no
 * transcript. Returns { text, roles, count }.
 */
export function inputPreview(messages, variables) {
  const msgs = Array.isArray(messages) ? messages.filter(Boolean) : [];
  if (msgs.length) {
    const lastUser = [...msgs].reverse().find((m) => m.role === 'user') || msgs[msgs.length - 1];
    const roles = [...new Set(msgs.map((m) => ROLE_TEXT[m.role] || m.role).filter(Boolean))];
    return { text: contentText(lastUser?.content) || '', roles, count: msgs.length };
  }
  const vars = variables && typeof variables === 'object' ? variables : {};
  const str = (v) => (typeof v === 'string' ? v : JSON.stringify(v));
  const pairs = Object.entries(vars).filter(([, v]) => v != null && v !== '').map(([k, v]) => [k, str(v)]);
  // One variable reads as the value itself; several read as key: value.
  const text = pairs.length === 1 ? pairs[0][1] : pairs.map(([k, v]) => `${k}: ${v}`).join(' · ');
  return { text, roles: [], count: 0, pairs };
}

/**
 * The Input cell's text, whole: every turn of the transcript as `role: text`,
 * one per line, or the variables as `key: value` lines (one variable reads as
 * its value). The table shows the row itself, not a summary of it.
 */
/**
 * A row's input as lines: its messages; else its text (the query a row added
 * as plain text keeps) and its named values.
 */
export function inputLines(messages, variables, query = null) {
  const msgs = Array.isArray(messages) ? messages.filter(Boolean) : [];
  if (msgs.length) {
    return msgs.map((m) => {
      const key = ROLE_TEXT[m.role] || m.role || 'message';
      const body = contentText(m.content) || (Array.isArray(m.tool_calls) && m.tool_calls.length
        ? m.tool_calls.map((t) => `→ ${t.function?.name || t.name || 'tool'}(${t.function?.arguments || JSON.stringify(t.arguments || {})})`).join('\n')
        : '');
      return { key, body };
    });
  }
  const vars = variables && typeof variables === 'object' ? variables : {};
  const str = (v) => (typeof v === 'string' ? v : JSON.stringify(v));
  const text = typeof query === 'string' && query.trim() ? [{ key: null, body: query }] : [];
  // A row copied from a run keeps its text both as the query and as an
  // `input` variable (for prompt binding): say it once.
  const pairs = Object.entries(vars).filter(([, v]) => v != null && v !== '' && !(text.length && str(v).trim() === query.trim()));
  if (pairs.length === 1 && !text.length) return [{ key: null, body: str(pairs[0][1]) }];
  return [...text, ...pairs.map(([k, v]) => ({ key: k, body: str(v) }))];
}

export function inputText(messages, variables, query = null) {
  return inputLines(messages, variables, query).map((l) => (l.key ? `${l.key}: ${l.body}` : l.body)).join('\n');
}

/** How many 17px lines `text` takes in a cell `width` px wide (an estimate from character counts). */
export function textLines(text, width) {
  if (!text) return 1;
  const perLine = Math.max(8, Math.floor((width - 16) / 6.6));
  return String(text).split('\n').reduce((n, line) => n + Math.max(1, Math.ceil(line.length / perLine)), 0);
}

/** Is this an eval verdict's output / correction ({ passed, steps })? */
export function isVerdictValue(v) {
  return v != null && typeof v === 'object' && !Array.isArray(v) && 'passed' in v && !('content' in v);
}

/** A verdict value in words: "Pass · UNGROUNDED · judge: Fail". */
export function verdictText(v) {
  if (!isVerdictValue(v)) return '';
  const bits = [v.passed === true ? 'Pass' : v.passed === false ? 'Fail' : '—'];
  if (v.reason_code) bits.push(v.reason_code);
  if (typeof v.score === 'number') bits.push(v.score.toFixed(2));
  if (v.fix_kind === 'eval') bits.push('missing a check');
  for (const [node, val] of Object.entries(v.steps || {})) {
    const s = val && typeof val === 'object' ? Object.values(val).map(String).join(', ') : String(val);
    bits.push(`${node}: ${s}`);
  }
  return bits.join(' · ');
}
